import { ShoppingBag03Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { format, startOfToday, subDays } from "date-fns";
import { lazy, Suspense, useCallback, useMemo } from "react";
import { PageContent } from "@/components/layout/page-content";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import type { DateRange } from "@/components/ui/range-date-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { HomeFilters } from "@/features/home/_components/home-filters";
import { KpiCardsGrid } from "@/features/home/_components/kpi-cards-grid";
import type { RevenueDataPoint } from "@/features/home/_components/revenue-chart";
import { TopProductsList } from "@/features/home/_components/top-products-list";

const RevenueChart = lazy(async () => {
	const m = await import("@/features/home/_components/revenue-chart");
	return { default: m.RevenueChart };
});

interface HomeSearchParams {
	startDate?: string;
	endDate?: string;
	branches?: string[];
}

export const Route = createFileRoute("/(dashboard)/")({
	component: HomePage,
	validateSearch: (search: Record<string, unknown>): HomeSearchParams => ({
		startDate:
			typeof search.startDate === "string" ? search.startDate : undefined,
		endDate: typeof search.endDate === "string" ? search.endDate : undefined,
		branches: Array.isArray(search.branches)
			? (search.branches as string[])
			: undefined,
	}),
});

function HomePage() {
	const navigate = useNavigate();
	const search = useSearch({ from: "/(dashboard)/" });

	const dateRange = useMemo<DateRange | undefined>(() => {
		if (search.startDate && search.endDate) {
			return {
				from: new Date(search.startDate),
				to: new Date(search.endDate),
			};
		}
		return {
			from: subDays(startOfToday(), 29),
			to: startOfToday(),
		};
	}, [search.startDate, search.endDate]);

	const selectedBranches = useMemo(
		() => search.branches ?? [],
		[search.branches],
	);

	const handleDateRangeChange = useCallback(
		(range: DateRange | undefined) => {
			navigate({
				to: ".",
				search: (prev) => ({
					...prev,
					startDate: range?.from ? format(range.from, "yyyy-MM-dd") : undefined,
					endDate: range?.to ? format(range.to, "yyyy-MM-dd") : undefined,
				}),
				replace: true,
			});
		},
		[navigate],
	);

	const handleBranchChange = useCallback(
		(branchIds: string[]) => {
			navigate({
				to: ".",
				search: (prev) => ({
					...prev,
					branches: branchIds.length > 0 ? branchIds : undefined,
				}),
				replace: true,
			});
		},
		[navigate],
	);

	const startDateParam = dateRange?.from
		? format(dateRange.from, "yyyy-MM-dd")
		: undefined;
	const endDateParam = dateRange?.to
		? format(dateRange.to, "yyyy-MM-dd")
		: undefined;

	const branchesQuery = useQuery(api.features.branches.web.list, {});
	const branches = branchesQuery ?? [];

	const dashboard = useQuery(
		api.features.analytics.web.getHomeDashboard,
		branchesQuery !== undefined && startDateParam && endDateParam
			? {
					branchIds:
						selectedBranches.length > 0
							? (selectedBranches as Id<"branches">[])
							: (branches.map((b) => b._id) as Id<"branches">[]),
					startDate: startDateParam,
					endDate: endDateParam,
				}
			: "skip",
	);

	const homeStats = dashboard?.homeStats;

	const branchOptions = useMemo(
		() => branches.map((b) => ({ value: b._id, label: b.name })),
		[branches],
	);

	const isLoading = dashboard === undefined || branchesQuery === undefined;
	const hasNoSales =
		!isLoading && (homeStats?.kpiSummary.transactionCount ?? 0) === 0;

	const chartBranches = useMemo(() => {
		const filteredBranches =
			selectedBranches.length > 0
				? branches.filter((b) => selectedBranches.includes(b._id))
				: branches;

		return filteredBranches.map((b) => ({
			id: b._id,
			name: b.name,
		}));
	}, [branches, selectedBranches]);

	const chartData = useMemo(() => {
		if (!homeStats?.revenueHistory) return [];

		type GroupedPoint = RevenueDataPoint & Record<string, string | number>;
		const grouped = new Map<string, GroupedPoint>();

		for (const entry of homeStats.revenueHistory) {
			const existing =
				grouped.get(entry.date) ||
				({
					date: entry.date,
					revenue: 0,
				} as GroupedPoint);

			existing[entry.branchId] = entry.revenue;

			existing.revenue = (existing.revenue as number) + entry.revenue;

			grouped.set(entry.date, existing);
		}

		return Array.from(grouped.values()).sort((a, b) =>
			a.date.localeCompare(b.date),
		);
	}, [homeStats?.revenueHistory]);

	const topProducts = useMemo(() => {
		if (!homeStats?.topProducts?.data) return [];
		return homeStats.topProducts.data.map((product) => ({
			productId: product.productId,
			name: product.name,
			revenue: product.revenue,
			sales: product.sales,
			imageUrl: product.imageUrl ?? undefined,
		}));
	}, [homeStats?.topProducts?.data]);

	return (
		<PageContent title="Home">
			<div className="flex justify-start mb-6">
				<HomeFilters
					branches={branchOptions}
					selectedBranches={selectedBranches}
					onBranchChange={handleBranchChange}
					dateRange={dateRange}
					onDateRangeChange={handleDateRangeChange}
				/>
			</div>

			<div className="space-y-6">
				<KpiCardsGrid
					revenue={homeStats?.kpiSummary.revenue ?? 0}
					avgTransactionValue={homeStats?.kpiSummary.avgOrderValue ?? 0}
					transactionCount={homeStats?.kpiSummary.transactionCount ?? 0}
					newCustomers={homeStats?.kpiSummary.newCustomers ?? 0}
					loading={isLoading}
				/>
				{hasNoSales ? (
					<Empty className="border bg-card py-12">
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<HugeiconsIcon
									icon={ShoppingBag03Icon}
									size={20}
									strokeWidth={1.5}
								/>
							</EmptyMedia>
							<EmptyTitle>No sales in this period</EmptyTitle>
							<EmptyDescription>
								Once your terminal rings up sales, revenue trends and your top
								products will show up here. Try a wider date range or check back
								after your first sale.
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
						<Suspense
							fallback={<Skeleton className="h-[520px] w-full rounded-lg" />}
						>
							<RevenueChart
								data={chartData}
								branches={chartBranches}
								loading={isLoading}
								summary={{
									revenue: homeStats?.kpiSummary.revenue,
									revenueGrowth: homeStats?.kpiSummary.growth.revenueGrowth,
									transactionCount: homeStats?.kpiSummary.transactionCount,
									transactionGrowth:
										homeStats?.kpiSummary.growth.transactionGrowth,
								}}
							/>
						</Suspense>
						<TopProductsList products={topProducts} loading={isLoading} />
					</div>
				)}
			</div>
		</PageContent>
	);
}
